// Formatting helpers for billing screens. Money is integer paise (₹1 = 100 paise); calendar dates
// are IST "YYYY-MM-DD" strings; instants are epoch ms or ISO strings and are shown in IST.

export type DateInput = string | number | Date | null | undefined;

const IST_TIME_ZONE = "Asia/Kolkata";
const IST_OFFSET_MS = 330 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const AMOUNT_PATTERN = /^(-)?(\d*)(?:\.(\d*))?$/;

const rupeesWhole = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const rupeesWithPaise = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const calendarDateFormatter = new Intl.DateTimeFormat("en-IN", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

const istDateFormatter = new Intl.DateTimeFormat("en-IN", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: IST_TIME_ZONE,
});

const istDateTimeFormatter = new Intl.DateTimeFormat("en-IN", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  timeZone: IST_TIME_ZONE,
});

const countFormat = new Intl.NumberFormat("en-IN");

/** 123456 → "1,23,456"; null and NaN become "—" so missing data never reads as zero. */
export const formatCount = (value: number | null | undefined): string =>
  typeof value === "number" && Number.isFinite(value) ? countFormat.format(value) : "—";

/** 2500000 → "₹25,000"; 12345678 → "₹1,23,456.78"; -50000 → "-₹500". */
export const formatINR = (paise: number | null | undefined): string => {
  const value = Math.round(Number(paise) || 0);
  const formatter = value % 100 === 0 ? rupeesWhole : rupeesWithPaise;
  return formatter.format(value / 100);
};

/**
 * Parses a rupee amount typed by an admin ("25,000", "1250.5", "₹ 99.99", 1.005) into integer
 * paise without floating-point drift (half-up on the third decimal). Returns null when the input
 * is empty or not a plain number.
 */
export const rupeesToPaise = (input: string | number | null | undefined): number | null => {
  if (input === null || input === undefined) {
    return null;
  }
  if (typeof input === "number" && !Number.isFinite(input)) {
    return null;
  }
  const cleaned = String(input).replace(/[₹,\s]/g, "");
  const match = AMOUNT_PATTERN.exec(cleaned);
  if (!match) {
    return null;
  }
  const negative = Boolean(match[1]);
  const whole = match[2] || "";
  const fraction = match[3] || "";
  if (!whole && !fraction) {
    return null;
  }
  const padded = `${fraction}000`;
  let paise = Number(whole || "0") * 100 + Number(padded.slice(0, 2));
  if (Number(padded.charAt(2)) >= 5) {
    paise += 1;
  }
  if (!Number.isSafeInteger(paise)) {
    return null;
  }
  return negative && paise !== 0 ? -paise : paise;
};

/** Paise → editable rupee string: 2500000 → "25000", 2500050 → "25000.50". */
export const paiseToRupeesInput = (paise: number | null | undefined): string => {
  const value = Math.round(Number(paise) || 0);
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const rupees = Math.floor(abs / 100);
  const fraction = abs % 100;
  return fraction === 0
    ? `${sign}${rupees}`
    : `${sign}${rupees}.${String(fraction).padStart(2, "0")}`;
};

export const isDateOnly = (value: unknown): value is string =>
  typeof value === "string" && DATE_ONLY_PATTERN.test(value);

const dateOnlyToUtcMs = (value: string): number | null => {
  const match = DATE_ONLY_PATTERN.exec(value);
  if (!match) {
    return null;
  }
  const time = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isFinite(time) ? time : null;
};

export const toDate = (value: DateInput): Date | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (isDateOnly(value)) {
    const time = dateOnlyToUtcMs(value);
    return time === null ? null : new Date(time);
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "2026-11-05" → "5 Nov 2026" (calendar date, no timezone shift); instants → IST date. */
export const formatDate = (value: DateInput, fallback = "—"): string => {
  if (isDateOnly(value)) {
    const time = dateOnlyToUtcMs(value);
    return time === null ? value : calendarDateFormatter.format(new Date(time));
  }
  const date = toDate(value);
  return date ? istDateFormatter.format(date) : fallback;
};

/** Instant → "6 Nov 2026, 10:00 am IST". */
export const formatDateTime = (value: DateInput, fallback = "—"): string => {
  if (isDateOnly(value)) {
    return formatDate(value, fallback);
  }
  const date = toDate(value);
  return date ? `${istDateTimeFormatter.format(date)} IST` : fallback;
};

const RELATIVE_UNITS: Array<{ limitMs: number; unitMs: number; label: string; plural: string }> = [
  { limitMs: 60 * 60 * 1000, unitMs: 60 * 1000, label: "min", plural: "min" },
  { limitMs: DAY_MS, unitMs: 60 * 60 * 1000, label: "h", plural: "h" },
  { limitMs: 30 * DAY_MS, unitMs: DAY_MS, label: "day", plural: "days" },
  { limitMs: 365 * DAY_MS, unitMs: 30 * DAY_MS, label: "month", plural: "months" },
  { limitMs: Number.POSITIVE_INFINITY, unitMs: 365 * DAY_MS, label: "year", plural: "years" },
];

/** "just now", "5 min ago", "3 h ago", "2 days ago", "in 4 days". */
export const formatRelative = (value: DateInput, fallback = "—", now: number = Date.now()) => {
  const date = toDate(value);
  if (!date) {
    return fallback;
  }
  const diffMs = date.getTime() - now;
  const absMs = Math.abs(diffMs);
  if (absMs < 45 * 1000) {
    return "just now";
  }
  const unit =
    RELATIVE_UNITS.find((candidate) => absMs < candidate.limitMs) ||
    RELATIVE_UNITS[RELATIVE_UNITS.length - 1];
  const count = Math.max(1, Math.round(absMs / unit.unitMs));
  const text = `${count} ${count === 1 ? unit.label : unit.plural}`;
  return diffMs > 0 ? `in ${text}` : `${text} ago`;
};

/** Today's calendar date in IST ("YYYY-MM-DD"). */
export const todayIST = (now: number = Date.now()): string =>
  new Date(now + IST_OFFSET_MS).toISOString().slice(0, 10);

/** Calendar maths on "YYYY-MM-DD" strings. */
export const addDaysToDate = (value: string, days: number): string => {
  const time = dateOnlyToUtcMs(value);
  if (time === null) {
    return value;
  }
  return new Date(time + days * DAY_MS).toISOString().slice(0, 10);
};

/** Whole days from `from` to `to` (both "YYYY-MM-DD"); null when either is invalid. */
export const daysBetween = (from: string, to: string): number | null => {
  const start = dateOnlyToUtcMs(from);
  const end = dateOnlyToUtcMs(to);
  if (start === null || end === null) {
    return null;
  }
  return Math.round((end - start) / DAY_MS);
};

/** "today", "yesterday" or "N days ago" for an IST date ("YYYY-MM-DD"); no hour precision. */
export const formatDaysAgo = (value: string, today: string = todayIST()): string => {
  const days = daysBetween(value, today);
  if (days === null || days < 0) {
    return formatDate(value);
  }
  if (days === 0) {
    return "today";
  }
  return days === 1 ? "yesterday" : `${days} days ago`;
};

export const pluralize = (count: number, singular: string, plural = `${singular}s`): string =>
  `${count} ${Math.abs(count) === 1 ? singular : plural}`;

/** Reminder offset → "7 days before", "on the due date", "1 day after". */
export const describeOffsetDays = (offset: number): string => {
  if (offset === 0) {
    return "on the due date";
  }
  return offset < 0 ? `${pluralize(-offset, "day")} before` : `${pluralize(offset, "day")} after`;
};

/** "snake_case_value" → "Snake case value". */
export const humanize = (value: string | null | undefined): string => {
  if (!value) {
    return "";
  }
  const text = value.replace(/[_-]+/g, " ").trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
};
